import * as Sentry from '@sentry/vue';
import {
  CallOptions,
  Client,
  ClientError,
  ClientMiddlewareCall,
  CompatServiceDefinition,
  createChannel,
  createClientFactory,
  Metadata,
  Status,
} from 'nice-grpc-web';
import ExtendableError from 'ts-error';
import useImpersonation from '@/hooks/useImpersonation';
import { unref } from 'vue';
import { useUserStore } from '@/store/user';
import missionControlVersion from '@/version';
import MaintenanceStatusService from 'hiber-grpc/src/services/maintenance-status';
import { useMaintenanceStore } from '@/store/maintenance';
import { getAuthProvider, getAuthProviderName } from '@/auth/AuthFactory';

declare global {
  interface Window {
    __GRPCWEB_DEVTOOLS__: () => void;
  }
}

const hasGrpcWebDevTools = () => window && typeof window.__GRPCWEB_DEVTOOLS__ === 'function';

export async function userCheck(): Promise<string> {
  const userStore = useUserStore();

  if (userStore.hasLocalAuthToken) {
    return `Bearer ${userStore.authToken}`;
  }

  if (!userStore.useLocalTokenAuth) {
    const token = await getAuthProvider().getToken();
    if (token) return `Bearer ${token}`;
  }

  throw new Error('User is not authenticated');
}

async function* grpcDevToolsMiddleware<Request, Response>(
  call: ClientMiddlewareCall<Request, Response>,
  options: CallOptions,
) {
  const { path } = call.method;

  try {
    const response = yield* call.next(call.request, options);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { request, ...rest } = response as any;
    if (hasGrpcWebDevTools()) {
      window.postMessage(
        {
          type: '__GRPCWEB_DEVTOOLS__',
          method: path,
          methodType: 'unary',
          request: call.request,
          response: response,
        },
        window.parent.origin,
      );
    }
    return response;
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { request, ...rest } = error;
    if (hasGrpcWebDevTools()) {
      window.postMessage(
        {
          type: '__GRPCWEB_DEVTOOLS__',
          method: path,
          methodType: 'unary',
          request: call.request,
          response: rest,
          error: rest,
        },
        window.parent.origin,
      );
    }
    throw error;
  }
}

async function* sentryLoggingMiddleware<Request, Response>(
  call: ClientMiddlewareCall<Request, Response>,
  options: CallOptions,
) {
  const maintenanceStore = useMaintenanceStore();
  const userStore = useUserStore();

  const currentUserEmail = userStore.user?.email;

  const organizationImpersonation = useImpersonation();

  const extraLog = {
    user: currentUserEmail,
    impersonation: unref(organizationImpersonation),
  };

  const { path } = call.method;
  try {
    return yield* call.next(call.request, options);
  } catch (error) {
    if (error instanceof ClientError) {
      if (import.meta.env.DEV) {
        console.group('gRPC Request failed');
        console.log(`%cThe call to ${path} has failed!`, 'color: #E02F2F; font-weight: bold; font-size: 12px');
        console.log(`request: %O`, JSON.stringify(call.request, null, 2));
        console.groupEnd();
      }

      if (error.code === Status.UNAUTHENTICATED) {
        const isOry = !userStore.hasLocalAuthToken && !userStore.useLocalTokenAuth && getAuthProviderName() === 'ory';

        if (isOry) {
          try {
            await getAuthProvider().getCurrentUser();
            // An active cookie means the backend JWT failed; keep the session.
            // A confirmed Ory 401 emits null and clears local state via App.
          } catch (sessionError) {
            Sentry.captureException(sessionError);
            // An Ory outage is not proof that the browser session expired.
          }
        } else {
          await userStore.signout();
        }
      }

      if (error.code === Status.FAILED_PRECONDITION) {
        const maintenanceStatus = await MaintenanceStatusService.getInstance(getGrpcClient).maintenance({});
        if (maintenanceStatus?.maintenanceMode) {
          maintenanceStore.setMaintenanceMode(maintenanceStatus.maintenanceMode);
        }
      }

      if (error.message === 'Response closed without headers') {
        // Connection error
        Sentry.captureException('Connection error: Response closed without headers.', {
          extra: { ...extraLog, call: path },
        });
      } else if (!error.message) {
        // List under its GRPC code error.
        Sentry.captureException(`${Status[error.code]} error with no message`, {
          extra: { ...extraLog, call: path },
        });
      } else {
        // Regular error
        Sentry.captureException(error.stack, {
          extra: { ...extraLog, call: path },
        });
      }
    } else if (error instanceof ExtendableError) {
      Sentry.captureException(error.stack, {
        extra: { call: path },
      });
    }
    throw error;
  }
}

async function* authMiddleware<Request, Response>(call: ClientMiddlewareCall<Request, Response>, options: CallOptions) {
  // eslint-disable-next-line no-useless-catch
  try {
    const auth = await userCheck();
    options.metadata = Metadata({ authorization: auth });
    const organizationImpersonation = useImpersonation();
    const organizationToImpersonate = unref(organizationImpersonation);

    const { organization, groups, groupContent } = call.request as any;

    // special case when the request includes group requests {groups:{}, groupContent:{}}
    const isGroupRequest =
      groups &&
      groupContent &&
      Object.prototype.hasOwnProperty.call(groups, 'organization') &&
      Object.prototype.hasOwnProperty.call(groupContent, 'organization');
    if (isGroupRequest) {
      groups.organization = organizationToImpersonate;
      groupContent.organization = organizationToImpersonate;
    }

    if (!organization) {
      (call.request as any).organization = organizationToImpersonate;
    }

    return yield* call.next(call.request, options);
  } catch (e) {
    throw e;
  }
}

async function* userAgentMiddleware<Request, Response>(
  call: ClientMiddlewareCall<Request, Response>,
  options: CallOptions,
) {
  const metadata = options.metadata ? options.metadata.clone() : new Metadata();
  metadata.set('user-agent', `HiberMissionControl/${missionControlVersion}`);
  metadata.set('X-Hiber-Mission-Control', missionControlVersion);
  return yield* call.next(call.request, { ...options, metadata });
}

const grpcClientFactory = createClientFactory()
  .use(sentryLoggingMiddleware)
  .use(authMiddleware)
  .use(grpcDevToolsMiddleware)
  .use(userAgentMiddleware);

const customerChannel = createChannel(import.meta.env.VITE_CUSTOMER_HOST);
const supportChannel = createChannel(import.meta.env.VITE_SUPPORT_HOST);

export const channels = {
  customer: customerChannel,
  support: supportChannel,
};

type ChannelsType = keyof typeof channels;

export const getGrpcFactory = <T extends CompatServiceDefinition>(
  channel: ChannelsType = 'customer',
): ((service: T) => Client<T>) => {
  const useChannel = channels[channel];

  return (service) => grpcClientFactory.create(service, useChannel);
};

export const getGrpcClient = <T extends CompatServiceDefinition>(
  service: T,
  options?: Record<string, any>,
  channel: ChannelsType = 'customer',
): Client<T> => {
  const useChannel = channels[channel];

  return grpcClientFactory.create(service, useChannel);
};
